/**
 * Discoverability Storage
 *
 * Client-side persistence for onboarding and feature discovery state.
 *
 * Primary responsibilities:
 * - Store seen tooltips, prompts, tours, and trigger counts
 * - Migrate legacy localStorage keys into the shared state
 *
 * Dependencies:
 * - browser localStorage
 */

export type DiscoverabilityState = {
  version: 1
  seenTooltips: Record<string, string>
  dismissedPrompts: Record<string, string>
  completedPrompts: Record<string, string>
  completedTours: Record<string, string>
  triggerCounts: Record<string, number>
  lastTriggeredAt: Record<string, string>
}

const STORAGE_PREFIX = 'losi:discoverability:v1'
const LEGACY_SEEN_FEATURES_KEY = 'seen-features'
const LEGACY_ONBOARDING_KEY = 'hasCompletedOnboarding'

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const createIsoTimestamp = () => new Date().toISOString()

export const createDiscoverabilityState = (): DiscoverabilityState => ({
  version: 1,
  seenTooltips: {},
  dismissedPrompts: {},
  completedPrompts: {},
  completedTours: {},
  triggerCounts: {},
  lastTriggeredAt: {}
})

export const getDiscoverabilityStorageKey = (userId?: string | null) => {
  return `${STORAGE_PREFIX}:${userId || 'guest'}`
}

const readStringRecord = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) return {}

  return Object.entries(value).reduce<Record<string, string>>((record, [key, item]) => {
    if (typeof item === 'string') {
      record[key] = item
    }

    return record
  }, {})
}

const readNumberRecord = (value: unknown): Record<string, number> => {
  if (!isRecord(value)) return {}

  return Object.entries(value).reduce<Record<string, number>>((record, [key, item]) => {
    if (typeof item === 'number' && Number.isFinite(item)) {
      record[key] = item
    }

    return record
  }, {})
}

const parseStorageRecord = (rawValue: string | null): Record<string, unknown> => {
  if (!rawValue) return {}

  try {
    const parsed: unknown = JSON.parse(rawValue)
    return isRecord(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const mergeLegacyState = (state: DiscoverabilityState) => {
  if (typeof window === 'undefined') return state

  const legacySeenFeatures = parseStorageRecord(
    window.localStorage.getItem(LEGACY_SEEN_FEATURES_KEY)
  )
  const seenTooltips = {
    ...readStringRecord(legacySeenFeatures),
    ...state.seenTooltips
  }

  const completedTours = { ...state.completedTours }
  if (window.localStorage.getItem(LEGACY_ONBOARDING_KEY) && !completedTours.main) {
    completedTours.main = createIsoTimestamp()
  }

  return {
    ...state,
    seenTooltips,
    completedTours
  }
}

export const loadDiscoverabilityState = (userId?: string | null): DiscoverabilityState => {
  const fallbackState = createDiscoverabilityState()

  if (typeof window === 'undefined') {
    return fallbackState
  }

  try {
    const storageKey = getDiscoverabilityStorageKey(userId)
    const rawState = parseStorageRecord(window.localStorage.getItem(storageKey))

    const loadedState: DiscoverabilityState = {
      version: 1,
      seenTooltips: readStringRecord(rawState.seenTooltips),
      dismissedPrompts: readStringRecord(rawState.dismissedPrompts),
      completedPrompts: readStringRecord(rawState.completedPrompts),
      completedTours: readStringRecord(rawState.completedTours),
      triggerCounts: readNumberRecord(rawState.triggerCounts),
      lastTriggeredAt: readStringRecord(rawState.lastTriggeredAt)
    }

    return mergeLegacyState(loadedState)
  } catch {
    return mergeLegacyState(fallbackState)
  }
}

export const saveDiscoverabilityState = (
  userId: string | null | undefined,
  state: DiscoverabilityState
) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(getDiscoverabilityStorageKey(userId), JSON.stringify(state))
  } catch {
    // Discovery should never block the app if storage is unavailable.
  }
}
